/**
 * PurchaseOrderModal — COM-02: Crear / Editar Orden de Compra
 */
import { useMemo, useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import DatePicker from '@/components/DatePicker'
import { Loader2, Plus, Trash2 } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'

/** Una línea de la OC, con su posición en el array. */
type FilaDeLinea = POLine & { idx: number }

/** El estilo de los campos de la tabla de líneas, que son todos iguales. */
const CELDA = 'px-2 py-1.5 text-sm border border-divider rounded-lg focus:outline-none focus:ring-2 focus:ring-primary w-full bg-surface'
import { useSuppliers, usePurchaseOrder, type POLine } from '../hooks/usePurchases'
import { formatRut } from '@/lib/utils'
import { inputCls, labelCls } from '@/lib/formStyles'
import { formatNumber, useTenantLocale, tenantCurrency, roundMoney } from '@/lib/locale'
import { useTenantTax } from '@/modules/config/hooks/useConfig'
import { Autocomplete, type AutocompleteItem } from '@/components/Autocomplete'
import { useAccounts } from '@/modules/accounting/hooks/useAccounting'
import { NumericInput } from '@/components/NumericInput'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { Modal } from '@/components/ds/Modal'

const CURRENCY_OPTIONS = [
  { value: 'CLP', label: 'CLP — Peso Chileno' },
  { value: 'USD', label: 'USD — Dólar Americano' },
  { value: 'UF',  label: 'UF — Unidad de Fomento' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'PEN', label: 'PEN — Sol Peruano' },
  { value: 'ARS', label: 'ARS — Peso Argentino' },
  { value: 'MXN', label: 'MXN — Peso Mexicano' },
  { value: 'COP', label: 'COP — Peso Colombiano' },
]

interface Props {
  orderId?: string | null
  onSave: (data: { supplier: string; lines: POLine[]; currency?: string; notes?: string; payment_terms?: string; expected_date?: string; expense_account?: string | null }) => void
  onClose: () => void
  isPending: boolean
  error?: string | null
}

type FormData = {
  supplier: string
  currency: string
  expected_date: string
  notes: string
  payment_terms: string
  expense_account: string
}

export function PurchaseOrderModal({ orderId, onSave, onClose, isPending, error }: Props) {
  const tax = useTenantTax()
  const { data: suppliersData } = useSuppliers({ active: 'true', page_size: 9999 })
  const { data: order, isLoading: orderLoading } = usePurchaseOrder(orderId ?? '')
  const [lines, setLines] = useState<POLine[]>([
    { sku: '', name: '', quantity: '1', unit_price: '0' },
  ])

  const locale = useTenantLocale()
  const { data: accountsData } = useAccounts({ is_active: 'true', is_imputable: 'true' })
  const accountItems: AutocompleteItem[] = (accountsData?.accounts ?? [])
    .filter(a => ['expense', 'asset'].includes(a.account_type))
    .map(a => ({ id: a.id, label: `${a.code} — ${a.name}` }))

  const { register, handleSubmit, reset, control, formState: { errors }, watch, setValue } = useForm<FormData>({
    defaultValues: { supplier: '', currency: tenantCurrency(), expected_date: '', notes: '', payment_terms: '', expense_account: '' },
  })

  const expenseAccountId = watch('expense_account')
  const currency = watch('currency')

  useEffect(() => {
    if (order) {
      reset({
        supplier: order.supplier,
        currency: order.currency ?? 'CLP',
        expected_date: order.expected_date ?? '',
        notes: order.notes,
        payment_terms: order.payment_terms ?? '',
        expense_account: order.expense_account ?? '',
      })
      setLines(order.lines?.length ? order.lines : [{ sku: '', name: '', quantity: '1', unit_price: '0' }])
    }
  }, [order, reset])

  const addLine = () => setLines(l => [...l, { sku: '', name: '', quantity: '1', unit_price: '0' }])
  const removeLine = (i: number) => setLines(l => l.filter((_, idx) => idx !== i))
  const updateLine = (i: number, field: keyof POLine, value: string | boolean) =>
    setLines(l => l.map((line, idx) => idx === i ? { ...line, [field]: value } : line))

  /** Las líneas con su posición adentro — ver el comentario de la tabla. */
  const filasDeLinea = useMemo<FilaDeLinea[]>(
    () => lines.map((l, idx) => ({ ...l, idx })),
    [lines],
  )

  const lineSubtotal = (l: POLine) => {
    const qty = parseFloat(l.quantity) || 0
    const price = parseFloat(l.unit_price) || 0
    return qty * price
  }
  const subtotal = lines.reduce((acc, l) => acc + lineSubtotal(l), 0)
  const taxableSubtotal = lines.reduce(
    (acc, l) => acc + (l.is_tax_exempt ? 0 : lineSubtotal(l)),
    0,
  )
  // Impuesto solo sobre líneas no exentas. La tasa sale de Configuración →
  // Localización, que es de donde la toma el backend al recalcular: con 0.19
  // escrito acá, un tenant con IGV 18% veía un total distinto del guardado.
  const taxAmount = roundMoney(taxableSubtotal * tax.rate / 100, tenantCurrency())
  const total = subtotal + taxAmount

  const onSubmit = (data: FormData) => {
    onSave({
      supplier: data.supplier,
      lines,
      currency: data.currency || 'CLP',
      notes: data.notes || undefined,
      payment_terms: data.payment_terms || undefined,
      expected_date: data.expected_date || undefined,
      expense_account: data.expense_account || null,
    })
  }

  // CLP típicamente no usa decimales (no hay centavos). Otras monedas sí.
  const amountDecimals = currency === 'CLP' ? 0 : 2
  const fmtAmount = (v: number) => formatNumber(v, locale, amountDecimals)

  return (
    <Modal open onClose={onClose} title={orderId ? 'Editar Orden de Compra' : 'Nueva Orden de Compra'} size="2xl">
      {orderLoading ? (
        <div className="flex items-center justify-center py-16 text-content-subtle">
          <Loader2 size={20} className="animate-spin mr-2" />Cargando orden...
        </div>
      ) : (
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
        <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-5">
        {error && (
          <div className="px-4 py-3 bg-danger-surface border border-danger-border rounded-lg text-sm text-danger-content">{error}</div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label htmlFor="purchaseordermodal-supplier" className="block text-xs font-medium text-content-muted mb-1">Proveedor *</label>
            <select id="purchaseordermodal-supplier" {...register('supplier', { required: 'Seleccione un proveedor' })} className={inputCls}>
              <option value="">Seleccionar proveedor...</option>
              {suppliersData?.suppliers.map(s => (
                <option key={s.id} value={s.id}>{s.legal_name} {s.tax_id ? `— ${formatRut(s.tax_id)}` : ''}</option>
              ))}
            </select>
            {errors.supplier && <p className="text-xs text-danger-solid mt-0.5">{errors.supplier.message}</p>}
          </div>
          <div>
            <label htmlFor="purchaseordermodal-expected-date" className="block text-xs font-medium text-content-muted mb-1">Fecha Esperada</label>
            <Controller name="expected_date" control={control} render={({ field }) => <DatePicker id="purchaseordermodal-expected-date" value={field.value} onChange={field.onChange} onBlur={field.onBlur} />} />
          </div>
          <SelectField
            label="Moneda"
            selectProps={register('currency')}
          >
            {CURRENCY_OPTIONS.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
            ))}

          </SelectField>
          <TextField
            className="col-span-2"
            label="Notas"
            placeholder="Instrucciones adicionales..."
            inputProps={register('notes')}
          />
          <TextField
            className="col-span-2"
            label="Términos y condiciones"
            placeholder="Ej: Pago a 30 días"
            inputProps={register('payment_terms')}
          />
          <div className="col-span-2">
            <label htmlFor="purchaseordermodal-cuenta-de-gasto-opcional" className={labelCls}>Cuenta de gasto (opcional)</label>
            <Autocomplete id="purchaseordermodal-cuenta-de-gasto-opcional"
              items={accountItems}
              value={expenseAccountId}
              displayValue={accountItems.find(i => i.id === expenseAccountId)?.label ?? ''}
              onChange={() => { /* selección vía onSelect */ }}
              onSelect={(item: AutocompleteItem) => setValue('expense_account', item.id)}
              placeholder="Heredar del proveedor / defaults…"
              className={inputCls}
            />
            {expenseAccountId && (
              <button
                type="button"
                onClick={() => setValue('expense_account', '')}
                className="mt-1 text-xs text-content-muted hover:text-content underline"
              >
                Limpiar (volver al default del proveedor)
              </button>
            )}
            <p className="text-xs text-content-subtle mt-1">
              Si la dejas vacía, al convertir a factura se usará la cuenta por defecto del proveedor o del plan de cuentas.
            </p>
          </div>
        </div>

        {/* Líneas */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-content-muted">Líneas de la Orden</label>
            <button type="button" onClick={addLine}
              className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary-hover">
              <Plus size={12} />Agregar línea
            </button>
          </div>
          {/*
            Las filas llevan su POSICIÓN adentro: `updateLine` y `removeLine`
            reciben el índice del array y el `render` recibe el de la fila ya
            ordenada. Con el índice en el dato los dos coinciden siempre.
            🔴 Aun así ninguna columna lleva `sortable`: el orden de las líneas
            es del documento — es como se imprime la orden de compra.
          */}
          <DataTable<FilaDeLinea>
            columns={[
              { key: 'sku', header: 'SKU', width: '14%',
                render: f => (
                  <input
                    value={f.sku}
                    onChange={e => updateLine(f.idx, 'sku', e.target.value)}
                    className={CELDA}
                    placeholder="SKU"
                    aria-label={`SKU de la línea ${f.idx + 1}`}
                  />
                ) },
              { key: 'name', header: 'Descripción',
                render: f => (
                  <input
                    value={f.name}
                    onChange={e => updateLine(f.idx, 'name', e.target.value)}
                    className={CELDA}
                    placeholder="Descripción del ítem"
                    aria-label={`Descripción de la línea ${f.idx + 1}`}
                  />
                ) },
              { key: 'quantity', header: 'Cantidad', numeric: true, width: '10%',
                render: f => (
                  <NumericInput
                    value={f.quantity}
                    onChange={v => updateLine(f.idx, 'quantity', v)}
                    locale={locale}
                    decimals={2}
                    placeholder="0"
                    aria-label={`Cantidad de la línea ${f.idx + 1}`}
                    className={`${CELDA} text-right`}
                  />
                ) },
              { key: 'unit_price', header: 'Precio unitario', numeric: true, width: '16%',
                render: f => (
                  <div className="flex items-center gap-1">
                    {currency !== 'CLP' && (
                      <span className="text-[9px] font-bold bg-primary-100 text-primary-hover rounded px-1 py-0.5 whitespace-nowrap select-none">
                        {currency}
                      </span>
                    )}
                    <NumericInput
                      value={f.unit_price}
                      onChange={v => updateLine(f.idx, 'unit_price', v)}
                      locale={locale}
                      decimals={currency === 'CLP' ? 0 : 2}
                      placeholder="0"
                      aria-label={`Precio unitario de la línea ${f.idx + 1}`}
                      className={`${CELDA} text-right flex-1`}
                    />
                  </div>
                ) },
              { key: 'subtotal', header: 'Subtotal', numeric: true, width: '16%',
                render: f => (
                  <span className="font-medium text-content">
                    <span className="text-content-subtle text-xs mr-1">{currency}</span>
                    {fmtAmount(lineSubtotal(f))}
                    {f.is_tax_exempt && (
                      <span className="block text-[10px] text-content-subtle font-normal mt-0.5">Exento</span>
                    )}
                  </span>
                ) },
              { key: 'is_tax_exempt', header: 'Exento', width: '8%',
                render: f => (
                  <div className="text-center">
                    <input
                      type="checkbox"
                      checked={!!f.is_tax_exempt}
                      onChange={e => updateLine(f.idx, 'is_tax_exempt', e.target.checked)}
                      className="h-4 w-4 rounded border-divider text-primary focus:ring-primary cursor-pointer"
                      aria-label={`Exento de ${tax.name} — línea ${f.idx + 1}`}
                    />
                  </div>
                ) },
              { key: 'quitar', header: '', width: '4%',
                render: f => (
                  <div className="text-right">
                    <button type="button" onClick={() => removeLine(f.idx)}
                      className="p-1.5 text-danger-solid hover:text-danger-content hover:bg-danger-surface rounded-lg transition-colors"
                      aria-label={`Eliminar la línea ${f.idx + 1}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                ) },
            ]}
            rows={filasDeLinea}
            rowKey={f => f.idx}
            caption="Líneas de la orden de compra"
          />
        </div>

        {/* Totales */}
        <div className="bg-glass-3 rounded-xl p-4 space-y-2 text-sm">
          <div className="flex justify-between text-content-muted">
            <span>Subtotal neto</span>
            <span><span className="text-content-subtle text-xs mr-1">{currency}</span>{fmtAmount(subtotal)}</span>
          </div>
          {taxableSubtotal !== subtotal && (
            <div className="flex justify-between text-content-subtle text-xs">
              <span>Subtotal gravable (sin exentos)</span>
              <span><span className="mr-1">{currency}</span>{fmtAmount(taxableSubtotal)}</span>
            </div>
          )}
          <div className="flex justify-between text-content-muted">
            <span>{`${tax.name} (${tax.rate}%)`}</span>
            <span><span className="text-content-subtle text-xs mr-1">{currency}</span>{fmtAmount(taxAmount)}</span>
          </div>
          <div className="flex justify-between font-semibold text-content border-t border-divider pt-2">
            <span>Total</span>
            <span><span className="text-content-subtle text-xs mr-1">{currency}</span>{fmtAmount(total)}</span>
          </div>
        </div>

        </div>
        <div className="shrink-0 flex gap-3 justify-end px-6 py-4 border-t border-divider rounded-b-2xl bg-surface">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm border border-divider rounded-lg hover:bg-soft">
            Cancelar
          </button>
          <button type="submit" disabled={isPending}
            className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover disabled:opacity-60">
            {isPending && <Loader2 size={13} className="animate-spin" />}
            {orderId ? 'Guardar Cambios' : 'Crear Orden'}
          </button>
        </div>
      </form>
      )}
    </Modal>
  )
}
